import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import useFeatureFlag from "@/hooks/useFeatureFlag";
import useOrganizationAiModel from "@/hooks/useOrganizationAiModel";
import type { AgentModelChoice } from "@cs_teamwaydev/mailvista-sdk_react";
import { Check, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

interface ModelSelectorProps {
    value: AgentModelChoice;
    onChange: (choice: AgentModelChoice) => void;
}

/**
 * The organization's model catalogue, greyed: models are picked in the settings (model management),
 * not here; the organization's one is marked. Read only by an IA admin (the catalogue route needs the
 * role), hence a component of its own, rendered for them only.
 */
const CatalogueModels = () => {
    const { t } = useTranslation();
    const { availableModels, currentModelId } = useOrganizationAiModel();

    return availableModels.map((model) => (
        <DropdownMenuItem
            key={model.id}
            className="items-start gap-2 rounded-md px-2 py-1.5"
            disabled
        >
            <span className="flex min-w-0 flex-1 flex-col">
                <span className="text-[13px] font-medium text-primary-darkest">
                    {model.publicName}
                </span>
                <span className="text-xs text-neutral-dark">
                    {t(
                        model.id === currentModelId
                            ? "vistassistant.model.organizationHint"
                            : "vistassistant.model.catalogueHint",
                    )}
                </span>
            </span>
        </DropdownMenuItem>
    ));
};

/**
 * The assistant's model, under the input box (step-based contract). « Auto » is the only choice: the
 * agent follows the model the organization picks in the settings. An IA admin also sees the
 * catalogue's models, greyed.
 */
const ModelSelector = ({ value, onChange }: ModelSelectorProps) => {
    const { t } = useTranslation();
    const { isModelManagementDisplayed } = useFeatureFlag();
    const name = t("vistassistant.model.auto");

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    aria-label={t("vistassistant.model.label", { model: name })}
                    className="flex h-7 flex-none items-center gap-1 rounded-md px-2 text-[13px] font-medium text-neutral-dark transition-colors hover:bg-primary-lightest hover:text-primary-darkest data-[state=open]:bg-primary-lightest [&_svg]:size-3"
                    type="button"
                >
                    {name}
                    <ChevronDown aria-hidden strokeWidth={2} />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 rounded-lg p-1">
                <DropdownMenuItem
                    className="items-start gap-2 rounded-md px-2 py-1.5"
                    onSelect={() => onChange("auto")}
                >
                    <span className="flex min-w-0 flex-1 flex-col">
                        <span className="text-[13px] font-medium text-primary-darkest">
                            {name}
                        </span>
                        <span className="text-xs text-neutral-dark">
                            {t("vistassistant.model.autoHint")}
                        </span>
                    </span>
                    {value === "auto" && (
                        <Check
                            aria-hidden
                            className="mt-0.5 size-3.5 flex-none text-primary-dark"
                        />
                    )}
                </DropdownMenuItem>
                {isModelManagementDisplayed && <CatalogueModels />}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default ModelSelector;
